import React, { useEffect, useState } from "react";
import SynchronizedLine from "./SynchronizedLine";

type Phase = "typing" | "pauseAfterTyping" | "deleting" | "pauseAfterDeleting";

interface SynchronizedTypewriterProps {
  lines?: string[];
  colors?: string[];
  totalTypingDuration?: number;
  totalDeletingDuration?: number;
  pauseDuration?: number;
}

const PHASES: Phase[] = [
  "typing",
  "pauseAfterTyping",
  "deleting",
  "pauseAfterDeleting",
];

const SynchronizedTypewriter: React.FC<SynchronizedTypewriterProps> = ({
  lines = [],
  colors = [],
  totalTypingDuration = 2000,
  totalDeletingDuration = 1000,
  pauseDuration = 1000,
}) => {
  const [phaseIndex, setPhaseIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);

  const currentPhase: Phase = PHASES[phaseIndex];

  useEffect(() => {
    let duration: number;

    switch (currentPhase) {
      case "typing":
        duration = totalTypingDuration;
        break;
      case "pauseAfterTyping":
      case "pauseAfterDeleting":
        duration = pauseDuration;
        break;
      case "deleting":
        duration = totalDeletingDuration;
        break;
      default:
        duration = 1000;
    }

    let animationFrame: number | null = null;
    let start: number | null = null;

    const tick = (now: number) => {
      if (!start) start = now;
      const elapsed = now - start;
      const ratio = Math.min(elapsed / duration, 1);
      setProgress(ratio);

      if (ratio < 1) {
        animationFrame = requestAnimationFrame(tick);
      } else {
        setProgress(0);
        setPhaseIndex((prev) => (prev + 1) % PHASES.length);
      }
    };

    animationFrame = requestAnimationFrame(tick);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [currentPhase, totalTypingDuration, totalDeletingDuration, pauseDuration]);

  return (
    <div className="w-100">
      {lines.map((text, idx) => (
        <SynchronizedLine
          key={idx}
          text={text}
          phase={currentPhase}
          progress={progress}
          color={colors[idx] || "white"}
        />
      ))}
    </div>
  );
};

export default SynchronizedTypewriter;
